import {formatDistanceToNow} from 'date-fns';
import {MessageSquareWarning} from 'lucide-react';
import {
  Badge,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyUrlButton} from '@/components/shared/CopyableUrl';
import {type RejectedSubmission} from '@/api/rejectedSubmissions';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {TruncatedText} from '@/components/shared/TruncatedText';
import {statusLabel, statusTone, toneBadge} from '@/lib/status';

const SOURCE_BADGE = {
  qc: {
    label: 'QC · pre-delivery',
    title: 'Rejected by QC review before it was delivered to the customer.'
  },
  qa: {
    label: 'QA · weekly',
    title: 'Sampled by Weekly QA after delivery and judged a false positive.'
  }
} as const;

const COLUMN_COUNT = 9;

export function RejectedSubmissionsTable({
  items,
  isLoading
}: {
  readonly items: RejectedSubmission[];
  readonly isLoading: boolean;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Source</TableHead>
          <TableHead>Content</TableHead>
          <TableHead>Policy Violation</TableHead>
          <TableHead>You said</TableHead>
          <TableHead>Reviewer correction</TableHead>
          <TableHead>Reviewer notes</TableHead>
          <TableHead>Reviewed by</TableHead>
          <TableHead>Reviewed</TableHead>
          <TableHead>Current status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                <Spinner />
                Loading…
              </div>
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && items.length === 0 ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} className="p-0">
              <Empty className="py-10">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <MessageSquareWarning />
                  </EmptyMedia>
                  <EmptyTitle>No rejected submissions</EmptyTitle>
                  <EmptyDescription>
                    Nothing you submitted has been rejected by QC or corrected by QA under these
                    filters.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </TableCell>
          </TableRow>
        ) : null}
        {items.map((item) => (
          <RejectedSubmissionRow key={`${item.source}-${item.id}`} item={item} />
        ))}
      </TableBody>
    </Table>
  );
}

function RejectedSubmissionRow({item}: {readonly item: RejectedSubmission}) {
  const source = SOURCE_BADGE[item.source];
  const title = item.title ?? item.item_external_id ?? item.submission_id;
  const status = statusLabel(item.current_delivery_state);

  return (
    <TableRow>
      <TableCell>
        <Badge variant={item.source === 'qc' ? 'secondary' : 'outline'} title={source.title}>
          {source.label}
        </Badge>
      </TableCell>
      <TableCell className="max-w-[260px] text-xs">
        <div className="flex items-center gap-1.5">
          <TruncatedText className="font-medium">{title}</TruncatedText>
          {item.url ? <CopyUrlButton url={item.url} className="shrink-0" /> : null}
        </div>
      </TableCell>
      <TableCell className="text-xs">{item.policy_violation}</TableCell>
      {/* Wraps, unlike a default cell: under max-w a nowrap line ("Child Safety - CSAM Consumer /
          CSAM Consumer") ran on over the next column. */}
      <TableCell className="max-w-[260px] min-w-[180px] text-xs break-words whitespace-normal">
        <p>
          {item.original_policy_violation} / {item.original_risk}
        </p>
        {item.original_notes ? (
          <p className="text-muted-foreground break-words whitespace-pre-wrap">
            {item.original_notes}
          </p>
        ) : null}
      </TableCell>
      <TableCell className="text-xs">{correction(item)}</TableCell>
      <TableCell className="max-w-[360px] min-w-[200px] text-xs break-words whitespace-pre-wrap">
        {item.reviewer_notes ?? '—'}
      </TableCell>
      <TableCell className="text-muted-foreground text-xs">{item.reviewed_by ?? '—'}</TableCell>
      <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
        {formatDistanceToNow(new Date(item.reviewed_at), {addSuffix: true})}
      </TableCell>
      <TableCell>
        {/* statusLabel only knows the submission states; anything else is shown as-is. */}
        {status === '—' ? (
          <span className="text-xs">{item.current_delivery_state ?? '—'}</span>
        ) : (
          <Badge {...toneBadge(statusTone(item.current_delivery_state))}>{status}</Badge>
        )}
      </TableCell>
    </TableRow>
  );
}

function correction(item: RejectedSubmission): string {
  if (item.source === 'qc') {
    return 'Rejected';
  }

  if (item.no_risk) {
    return 'No Risk';
  }

  return item.corrected_policy_violation && item.corrected_risk
    ? `QA says ${item.corrected_policy_violation} / ${item.corrected_risk}`
    : 'False positive';
}
